import React, { useState, useEffect, useRef, useCallback } from "react"

import { Link, useLocation } from "react-router-dom";

import Menu from "./Menu/Menu";
import Reports from "./Reports";
import { saveAs } from "file-saver";

import ViewerEmbed from "./ViewerEmbed";
import Certificate from './Certificate';
import Scope from "./Scope";
import ScopesStatus from "./ScopesStatus";

import ViewerStyle from '../css/Viewer.module.css'
import ScopeStyle from '../css/Scope.module.css'
import ScopesStatusStyle from '../css/ScopesStatus.module.css'

import logo from '../images/logo-cnc.png';

import { API_DEFAULT } from "../services/settings";
import { API } from "../services/routes";
import { DOCUMENTS, fetchDocument } from "../services/documents";
import { sortViewers, groupViewers, viewerRoute, viewerRoutes, populationTabs } from "../services/viewers";
import { useSession } from "../services/session";
import { request, tokenExpired } from "../services/http";


function Viewer(props) {

  const { setAccessToken, setRefreshToken, setAuth, accessToken, refreshToken, showUser, setShowLoader, setShowAlert, setMessage, setSeverity } = props;

  const [paths, setPaths] = props.paths;
  const location = useLocation();

  // The refresh, the sign-out and the end of a session (services/session.js).
  const { getAccessTokenWithRefresh, logOut } = useSession({
    accessToken, refreshToken, setAccessToken, setRefreshToken, setAuth, setPaths,
    setShowLoader, setShowAlert, setMessage, setSeverity,
  });

  const [dataViewers, setDataViewers] = useState([])
  const [index, setIndex] = useState()
  const selectedViewer = dataViewers[index]

  const [showIconReport, setShowIconReport] = useState(false)
  const [certificateOpen, setCertificateOpen] = useState(false)
  // How many times the menu has been opened: the menu brings the selected
  // button into sight when it is first shown (Menu/MenuLatest.jsx).
  const [menuOpenings, setMenuOpenings] = useState(0)

  const [viewersLatest, setViewersLatest] = useState([])
  const [viewersModelo1, setViewersModelo1] = useState([])
  const [viewersModelo0, setViewersModelo0] = useState([])
  const [viewersUniversoClaro, setViewersUniversoClaro] = useState([])
  const [viewersCincoMinutos, setViewersCincoMinutos] = useState([])
  const [othersViewers, setOthersViewers] = useState([])

  const scopeStatusRef = useRef(null);
  const reportsRef = useRef(null);

  const handleButtonClick = async () => {
    if (scopeStatusRef.current) {
      await scopeStatusRef.current.getScopesProcess(accessToken);
    }
  };

  // The viewers this user's organisation may open.
  // Built once: everything it uses keeps its identity across renders, so the
  // effect below can list it without running on every render.
  const getViewers = useCallback(async (access) => {
    const answer = await request(
      `${API_DEFAULT}/${API.viewers}`, {
      headers: {
        "Authorization": "Bearer " + access
      }
    }
    )

    if (tokenExpired(answer)) {
      let newAccess = await getAccessTokenWithRefresh();
      if (newAccess) {
        setAccessToken(newAccess);
        localStorage.setItem('accessToken', newAccess);
      }
    }
    else if (answer.ok && Array.isArray(answer.data)) {
      const sortedData = sortViewers(answer.data);

      const groups = groupViewers(sortedData);
      setViewersLatest(groups.latest)
      setViewersModelo1(groups.modelo1)
      setViewersModelo0(groups.modelo0)
      setViewersUniversoClaro(groups.universoClaro)
      setViewersCincoMinutos(groups.cincoMinutos)
      setOthersViewers(groups.others)

      const customPaths = viewerRoutes(sortedData);

      setPaths(customPaths);
      localStorage.setItem('customPaths', customPaths);

      setDataViewers(sortedData)

      setIndex(0)
    }
    else {
      // Without the list there is no viewer to embed, and the loader turned on
      // for it used to stay on with nothing said.
      setShowLoader(false);
      setMessage("No fue posible cargar los visores. Recargue la página o intente de nuevo más tarde.");
      setSeverity("danger");
      setTimeout(() => { setShowAlert(true) }, 0);
    }
  }, [getAccessTokenWithRefresh, setAccessToken, setPaths, setShowLoader, setMessage, setSeverity, setShowAlert])

  // The viewer of the other population, when the user's organisation has it
  // (services/viewers.js).
  const {
    show: showPopulationTabs,
    households: householdsViewer,
    people: peopleViewer,
  } = populationTabs(selectedViewer, dataViewers);

  // The tab of the viewer on screen is the selected one: the stylesheet greys
  // it out and makes it unclickable. It used to be set by hand on the DOM
  // after every route change, which missed the first render.
  const tabButtonClass = (viewer) =>
    `${ViewerStyle["tab-button"]}${viewer === selectedViewer ? ` ${ViewerStyle["tab-button-selected"]}` : ""}`;

  const tabMenu =
    <div className={`${ViewerStyle["tab-menu"]}${showPopulationTabs ? ` ${ViewerStyle["show-tab-menu"]}` : ""}`}>
      {showPopulationTabs ?
        <>
          <Link to={viewerRoute(householdsViewer)} onClick={() => changeViewer(dataViewers.indexOf(householdsViewer))} className={tabButtonClass(householdsViewer)}>
            <ion-icon name="home-outline"></ion-icon>
            <span>HOGARES</span>
          </Link>
          <Link to={viewerRoute(peopleViewer)} onClick={() => changeViewer(dataViewers.indexOf(peopleViewer))} className={tabButtonClass(peopleViewer)}>
            <ion-icon name="accessibility-outline"></ion-icon>
            <span>PERSONAS</span>
          </Link>
        </>
        : null}
    </div>


  const iconReport =
    <button title="Descargar reportes" onClick={() => reportsRef.current.show()} className={ViewerStyle["btn-report"]}>
      <ion-icon className={ViewerStyle["header-icon-report"]} name="cloud-download-outline"></ion-icon>
    </button>


  // Asked for once, and again with a fresh token while there is no list: that
  // is how a load that met an expired token finishes, and a load that failed
  // is tried again at the next refresh instead of leaving the portal empty.
  // An empty token is a session that has ended: the sign-off empties it while
  // this is still on screen, and asking with it put the back's refusal over
  // the message that the session had expired.
  useEffect(() => {
    if (accessToken && dataViewers.length === 0) {
      getViewers(accessToken)
    }
  }, [accessToken, dataViewers.length, getViewers]);

  // The viewer on screen follows the route.
  useEffect(() => {
    setIndex(paths.indexOf(location.pathname) - 2);
  }, [location, dataViewers, paths]);


  // The viewer on screen, by its place in the sorted catalogue.
  const changeViewer = (newIndex) => {
    setIndex(newIndex);
  }

  // const buttons = [...document.getElementsByClassName(ViewerStyle["btn-month"])];
  // buttons.forEach((button) => {
  //   button.onclick = function (e) {
  //     let x = e.clientX - e.target.offsetLeft;
  //     let y = e.clientY - e.target.offsetTop;
  //     let ripple = document.createElement("span");
  //     ripple.style.left = `${x}px`;
  //     ripple.style.top = `${y}px`;
  //     this.appendChild(ripple);
  //     setTimeout(function () {
  //       ripple.remove();
  //     }, 600);
  //   }
  // });

  // Opens the menu of viewers.
  const showMenu = () => {
    const modal = document.getElementById('modal-menu')
    modal.classList.remove(ViewerStyle["hide-modal-menu"])
    modal.classList.toggle(ViewerStyle["show-modal-menu"])

    const menu = document.getElementById('menu-container')
    menu.classList.toggle(ViewerStyle["menu-transition"])
    setMenuOpenings((openings) => openings + 1)
  }

  // Closes it.
  const hideMenu = () => {
    const menu = document.getElementById('menu-container')
    menu.classList.remove(ViewerStyle["menu-transition"])

    const modal = document.getElementById('modal-menu')
    setTimeout(function () {
      modal.classList.replace(ViewerStyle["show-modal-menu"], ViewerStyle["hide-modal-menu"])
    }, 400);
  }

  // ESC closes the menu, as clicking outside it does.
  const closeMenuWithEsc = (e) => {
    var tecla = e.keyCode;
    if (tecla === 27) {
      return hideMenu()
    }
  }
  window.onkeydown = closeMenuWithEsc;

  const showScope = () => {
    const scope = document.getElementById('scope')
    scope.classList.remove(ScopeStyle["hide-scope"])
    scope.classList.toggle(ScopeStyle["show-scope"])
  }

  const showScopesStatus = () => {
    handleButtonClick()
    const scopesStatus = document.getElementById('scopes-status')
    scopesStatus.classList.remove(ScopesStatusStyle["hide-scopes-status"])
    scopesStatus.classList.toggle(ScopesStatusStyle["show-scopes-status"])
  }

  // Open or closed is state now, so the certificate knows when to ask the
  // back for a URL: the one it gets lives fifteen minutes.
  const showCertificate = () => setCertificateOpen(true);

  const downloadGuide = async () => {
    setShowLoader(true);
    const saved = await fetchDocument(DOCUMENTS.userGuide, saveAs, { accessToken, setAccessToken, getAccessTokenWithRefresh });
    setShowLoader(false);
    if (!saved) {
      // It used to save whatever came back, so a 403 reached the client as a
      // PDF that was Google's XML error.
      setMessage("No fue posible descargar el manual. Intente de nuevo más tarde.");
      setSeverity("danger");
      setTimeout(() => { setShowAlert(true) }, 0);
    }
  }

  return (
    <>
      <header className={ViewerStyle["header-container"]}>
        <div className={ViewerStyle["header-left"]}>
          <button title="Menú" onClick={() => showMenu()} className={ViewerStyle["btn-menu"]}>
            <ion-icon className={ViewerStyle["header-icon-menu"]} name="menu-outline" />
          </button>
          <a href="https://www.centronacionaldeconsultoria.com/" target="_blank" rel="noreferrer">
            <img className={ViewerStyle["header-logo"]} src={logo} alt="Centro Nacional de Consultoría" />
          </a>
          <h3 className={ViewerStyle["header-title"]}><span className={ViewerStyle["header-title-brand"]}>CNC </span>Ratings</h3>
        </div>
        <div className={ViewerStyle["header-right"]}>
          {showIconReport ? iconReport : null}

          <button title="Generar consulta" onClick={() => showScope()} className={ViewerStyle["btn-report"]}>
            <ion-icon className={ViewerStyle["header-icon-report"]} name="stats-chart-outline"></ion-icon>
          </button>

          <button title="Status de consultas" onClick={() => showScopesStatus()} className={ViewerStyle["btn-report"]}>
            <ion-icon className={ViewerStyle["header-icon-report"]} name="pulse-outline"></ion-icon>
          </button>

          <button title="Ver Certificado" onClick={() => showCertificate()} className={ViewerStyle["btn-report"]}>
            <ion-icon className={ViewerStyle["header-icon-report"]} name="reader-outline"></ion-icon>
          </button>

          <button title="Descargar Manual" onClick={() => downloadGuide()} className={ViewerStyle["btn-report"]}>
            <ion-icon className={ViewerStyle["header-icon-report"]} name="book-outline"></ion-icon>
          </button>

          <h3>{showUser}</h3>

          <button title="Cerrar sesión" onClick={() => logOut()} className={ViewerStyle["btn-exit"]}>
            <ion-icon className={ViewerStyle["header-icon-exit"]} name="exit-outline" />
          </button>
        </div>
      </header>

      <body className={ViewerStyle["viewer-body"]}>
        <main className={ViewerStyle["viewer-main"]}>

          {tabMenu}

          <ViewerEmbed
            viewer={selectedViewer}
            catalogue={dataViewers}
            wide={!showPopulationTabs}
            accessToken={accessToken}
            setAccessToken={setAccessToken}
            getAccessTokenWithRefresh={getAccessTokenWithRefresh}
            setShowLoader={setShowLoader}
            setShowAlert={setShowAlert}
            setMessage={setMessage}
            setSeverity={setSeverity}
            onEmbedded={() => hideMenu()}
          />
          <div className={ViewerStyle["copyright-container"]}>
            <p className={ViewerStyle["copyright-text"]}>{"Copyright © "}
              <a href="https://www.centronacionaldeconsultoria.com/" target="_blank" rel="noreferrer">
                Centro Nacional de Consultoría (CNC)
              </a> 2022
            </p>
          </div>
        </main>

        <Menu
          viewersLatest={viewersLatest}
          viewersModelo1={viewersModelo1}
          viewersModelo0={viewersModelo0}
          viewersUniversoClaro={viewersUniversoClaro}
          viewersCincoMinutos={viewersCincoMinutos}
          othersViewers={othersViewers}
          changeViewer={changeViewer}
          location={location}
          dataViewers={dataViewers}
          menuOpenings={menuOpenings}
        />

        <Reports
          setAccessToken={setAccessToken}
          accessToken={accessToken}
          getAccessTokenWithRefresh={getAccessTokenWithRefresh}
          setShowLoader={setShowLoader}
          setShowAlert={setShowAlert}
          setMessage={setMessage}
          setSeverity={setSeverity}
          onHasReports={setShowIconReport}
          ref={reportsRef}
        />

        <Scope
          setAccessToken={setAccessToken}
          accessToken={accessToken}
          getAccessTokenWithRefresh={getAccessTokenWithRefresh}
          setShowLoader={setShowLoader}
          setShowAlert={setShowAlert}
          setMessage={setMessage}
          setSeverity={setSeverity}
        // channelPrograms={channelPrograms}
        // unificados={[unifiedPrograms, setUnifiedPrograms]}
        />

        <ScopesStatus
          setAccessToken={setAccessToken}
          accessToken={accessToken}
          getAccessTokenWithRefresh={getAccessTokenWithRefresh}
          setShowLoader={setShowLoader}
          setShowAlert={setShowAlert}
          setMessage={setMessage}
          setSeverity={setSeverity}
          ref={scopeStatusRef}
        // channelPrograms={channelPrograms}
        // unificados={[unifiedPrograms, setUnifiedPrograms]}
        />

        <Certificate
          open={certificateOpen}
          onClose={() => setCertificateOpen(false)}
          accessToken={accessToken}
          setAccessToken={setAccessToken}
          getAccessTokenWithRefresh={getAccessTokenWithRefresh}
          setMessage={setMessage}
          setSeverity={setSeverity}
          setShowAlert={setShowAlert}
        />

      </body>
    </>
  );

}

export default Viewer;